import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import axiosGeneral from "../../utils/axiosGeneral";
import axiosWithAuth from "../../utils/axiosWithAuth";
import ListingItem from "./ListingItem";

import {
  Radio,
  Pagination,
  Divider,
  Button,
  Modal,
  Input,
  message,
  Slider,
} from "antd";
import { EnvironmentOutlined, EditOutlined } from "@ant-design/icons";
import { useLocation } from "react-router-dom";

export default function RestaurantListing(props) {
  const [businesses, setBusinesses] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [sorting, setSorting] = useState("alphabetical");
  const [value, setValue] = useState("asc");
  const search = useLocation().search;
  const keyword = new URLSearchParams(search).get("keyword");
  const location = new URLSearchParams(search).get("location");
  const category = new URLSearchParams(search).get("category");
  const ratings = new URLSearchParams(search).get("ratings");
  const tags = new URLSearchParams(search).get("tags");
  const status = new URLSearchParams(search).get("status");
  const user = useSelector((state) => state.user.user);
  const [userRoleAccess, setUserRoleAccess] = useState([]);

  // State to determine when a user is logged in
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [businessesLength, setBusinessesLength] = useState(0);
  const [isLoader, setIsLoader] = useState(false);
  const pageCursorsRef = useRef({});
  const prevSearchRef = useRef(search);
  const prevSortingRef = useRef(sorting);
  const prevValueRef = useRef(value);
  const prevAppliedAddressRef = useRef("");
  const prevAppliedMaxDistanceRef = useRef(20);

  // Address state management - Load from localStorage on initialization
  const [customerAddress, setCustomerAddress] = useState(() => {
    return localStorage.getItem("customerAddress") || "";
  });
  const [appliedAddress, setAppliedAddress] = useState(() => {
    return localStorage.getItem("appliedAddress") || "";
  });
  const [isAddressModalVisible, setIsAddressModalVisible] = useState(false);
  const [tempAddress, setTempAddress] = useState("");
  const [hasSeenAddressPrompt, setHasSeenAddressPrompt] = useState(() => {
    return localStorage.getItem("hasSeenAddressPrompt") === "true";
  });

  // Max distance state - Default 20 miles
  const [maxDistance, setMaxDistance] = useState(() => {
    const saved = localStorage.getItem("maxDistance");
    return saved ? parseInt(saved) : 20;
  });
  const [appliedMaxDistance, setAppliedMaxDistance] = useState(() => {
    const saved = localStorage.getItem("appliedMaxDistance");
    return saved ? parseInt(saved) : 20;
  });

  // Backend pagination: first page is requested without cursor params.
  // Subsequent pages must send the cursor fields from the previous response.
  const buildRestaurantListUrl = (cursor, overrides = {}) => {
    const params = new URLSearchParams();

    if (
      cursor?.lastHasOwner !== undefined &&
      cursor?.lastName !== undefined &&
      cursor?.lastId !== undefined
    ) {
      params.set("lastHasOwner", cursor.lastHasOwner);
      params.set("lastName", cursor.lastName);
      params.set("lastId", cursor.lastId);
    }

    if (location) params.set("location", location);
    if (keyword) params.set("keyword", keyword);
    if (category) params.set("category", category);
    if (ratings) params.set("ratings", ratings);
    if (tags) params.set("tags", tags);
    if (status) params.set("status", status);

    params.set("sort", overrides.sort || sorting);
    params.set("OrderBy", overrides.orderBy || value);

    if (appliedAddress) {
      params.set("customerAddress", appliedAddress);
      params.set("maxDistance", appliedMaxDistance);
    }

    const query = params.toString();
    return query
      ? `/api/business/list/restaurant/?${query}`
      : `/api/business/list/restaurant/`;
  };

  const getCursorForPage = async (targetPage) => {
    if (targetPage <= 1) return null;
    if (pageCursorsRef.current[targetPage]) {
      return pageCursorsRef.current[targetPage];
    }

    let cursor = null;
    let cursorPage = 1;

    while (cursorPage < targetPage) {
      const response = await axiosGeneral().get(buildRestaurantListUrl(cursor));
      const nextCursor = response?.data?.nextCursor || null;

      pageCursorsRef.current[cursorPage + 1] = nextCursor;

      if (!nextCursor) {
        return null;
      }

      cursor = nextCursor;
      cursorPage += 1;
    }

    return cursor;
  };

  const resetRestaurantPagination = () => {
    pageCursorsRef.current = {};
    setCurrentPage(1);
  };

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    ScrollTrigger.create({
      trigger: "#load-more",
      onEnter: (self) => {
        console.log("Load More", self.isActive);
      },
      onLeave: (self) => {
        console.log("Stop loading more", self.isActive);
      },
    });
  }, []);

  useEffect(() => {
    const searchChanged = prevSearchRef.current !== search;
    const sortingChanged = prevSortingRef.current !== sorting;
    const valueChanged = prevValueRef.current !== value;
    const addressChanged = prevAppliedAddressRef.current !== appliedAddress;
    const distanceChanged =
      prevAppliedMaxDistanceRef.current !== appliedMaxDistance;

    if (
      searchChanged ||
      sortingChanged ||
      valueChanged ||
      addressChanged ||
      distanceChanged
    ) {
      pageCursorsRef.current = {};
      prevSearchRef.current = search;
      prevSortingRef.current = sorting;
      prevValueRef.current = value;
      prevAppliedAddressRef.current = appliedAddress;
      prevAppliedMaxDistanceRef.current = appliedMaxDistance;

      if (currentPage !== 1) {
        setCurrentPage(1);
        return;
      }
    }

    const fetchBusinesses = async () => {
      let cursor = null;

      try {
        setIsLoader(true);

        if (currentPage > 1) {
          cursor = pageCursorsRef.current[currentPage] || null;
          if (!cursor) {
            cursor = await getCursorForPage(currentPage);
          }
        }

        const response = await axiosGeneral().get(
          buildRestaurantListUrl(cursor),
        );

        if (response && response.status === 200 && response.data.businesses) {
          setBusinesses(response.data.businesses);
          setBusinessesLength(response.data.length);

          if (response.data.nextCursor) {
            pageCursorsRef.current[currentPage + 1] = response.data.nextCursor;
          }

          getAllTags();
        }
      } catch (err) {
        console.log(err.response);
      } finally {
        setIsLoader(false);
      }
    };

    fetchBusinesses();
  }, [search, currentPage, sorting, value, appliedAddress, appliedMaxDistance]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Logic for checking if have an auth token
    const getToken = JSON.parse(localStorage.getItem("token"));

    if (getToken) {
      if ("token" in getToken) {
        setIsLoggedIn(true);
      }
    }
  }, []);

  // Show address modal automatically if no address is set and user hasn't dismissed it
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!customerAddress && !isAddressModalVisible && !hasSeenAddressPrompt) {
        setIsAddressModalVisible(true);
        setTempAddress("");
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [customerAddress, isAddressModalVisible, hasSeenAddressPrompt]);

  useEffect(() => {
    if (isLoggedIn || !userRoleAccess.length) {
      getUserRoleAccess();
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const getUserRoleAccess = async () => {
    if (user && user.id) {
      const response = await axiosWithAuth().get(
        `${process.env.REACT_APP_BE_API_URL}/api/users/roleAccessBusiness`,
      );
      if (response && response.status == 200) {
        setUserRoleAccess(response.data);
      }
    }
  };

  const getAllTags = async () => {
    const response = await axiosWithAuth().get(
      `${process.env.REACT_APP_BE_API_URL}/api/business/getTags/type/business`,
    );
    if (response && response.status === 200) {
      props.onTagList(response.data);
    }
  };

  const handleSorting = (val) => {
    setIsLoader(true);
    resetRestaurantPagination();
    setSorting(val);
    setValue(value || "asc");
  };

  const onChange = (e) => {
    setIsLoader(true);
    resetRestaurantPagination();
    setSorting(sorting || "alphabetical");
    setValue(e.target.value);
  };

  if (isLoader) {
    return <div className="loader" id="loader"></div>;
  } else {
    return (
      <div id="directory-listing" className="listing-page">
        <div className="address-selector-wrapper">
          <div
            className="address-chip"
            onClick={() => {
              setIsAddressModalVisible(true);
              setTempAddress(customerAddress);
            }}
          >
            <EnvironmentOutlined className="address-icon" />
            <div className="address-chip-content">
              <span className="address-chip-label">
                {customerAddress ? "Your location" : "Set your location"}
              </span>
              {customerAddress && (
                <span className="address-chip-value">{customerAddress}</span>
              )}
            </div>
            <EditOutlined className="address-edit-icon" />
          </div>

          {customerAddress && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                minWidth: "250px",
              }}
            >
              <span
                style={{
                  fontSize: "14px",
                  color: "#595959",
                  whiteSpace: "nowrap",
                }}
              >
                Max: {maxDistance} mi
              </span>
              <Slider
                min={0}
                max={50}
                value={maxDistance}
                onChange={(newValue) => {
                  setMaxDistance(newValue);
                  localStorage.setItem("maxDistance", newValue.toString());
                }}
                style={{ flex: 1, margin: 0 }}
              />
            </div>
          )}

          <div style={{ marginLeft: "auto", display: "flex", gap: "8px" }}>
            {customerAddress &&
              (customerAddress !== appliedAddress ||
                maxDistance !== appliedMaxDistance) && (
                <Button
                  type="primary"
                  onClick={() => {
                    setAppliedAddress(customerAddress);
                    setAppliedMaxDistance(maxDistance);
                    localStorage.setItem("appliedAddress", customerAddress);
                    localStorage.setItem(
                      "appliedMaxDistance",
                      maxDistance.toString(),
                    );
                    setCurrentPage(1);
                    message.success("Finding restaurants near you...");
                  }}
                >
                  Apply
                </Button>
              )}

            {appliedAddress && (
              <Button
                type="text"
                danger
                onClick={() => {
                  setAppliedAddress("");
                  setAppliedMaxDistance(20);
                  setMaxDistance(20);
                  localStorage.removeItem("appliedAddress");
                  localStorage.removeItem("appliedMaxDistance");
                  localStorage.setItem("maxDistance", "20");
                  setCurrentPage(1);
                  message.info("Location filter cleared");
                }}
              >
                Clear
              </Button>
            )}
          </div>
        </div>

        <Modal
          title="Set Your Location"
          visible={isAddressModalVisible}
          open={isAddressModalVisible}
          onOk={() => {
            const trimmedAddress = tempAddress.trim();
            if (trimmedAddress) {
              setCustomerAddress(trimmedAddress);
              localStorage.setItem("customerAddress", trimmedAddress);
              setIsAddressModalVisible(false);
              setHasSeenAddressPrompt(true);
              localStorage.setItem("hasSeenAddressPrompt", "true");
              message.success(
                'Location saved! Click "Apply" to filter restaurants.',
              );
            } else {
              message.warning("Please enter a valid location");
            }
          }}
          onCancel={() => {
            setIsAddressModalVisible(false);
            setTempAddress(customerAddress);
            setHasSeenAddressPrompt(true);
            localStorage.setItem("hasSeenAddressPrompt", "true");
          }}
          okText="Save"
          cancelText="Cancel"
          width={500}
          className="address-modal"
        >
          <div className="address-modal-content">
            <p className="address-modal-description">
              Enter your address to find nearby restaurants
            </p>
            <Input
              placeholder="e.g., 123 Main Street, New York, NY 10001"
              value={tempAddress}
              onChange={(e) => setTempAddress(e.target.value)}
              size="large"
              prefix={<EnvironmentOutlined style={{ color: "#bfbfbf" }} />}
              onPressEnter={() => {
                const trimmedAddress = tempAddress.trim();
                if (trimmedAddress) {
                  setCustomerAddress(trimmedAddress);
                  localStorage.setItem("customerAddress", trimmedAddress);
                  setIsAddressModalVisible(false);
                  setHasSeenAddressPrompt(true);
                  localStorage.setItem("hasSeenAddressPrompt", "true");
                  message.success(
                    'Location saved! Click "Apply" to filter restaurants.',
                  );
                } else {
                  message.warning("Please enter a valid location");
                }
              }}
            />
            <p className="address-modal-hint">
              Coming soon: Google Maps autocomplete
            </p>
          </div>
          {customerAddress && (
            <Button
              type="link"
              danger
              onClick={() => {
                setCustomerAddress("");
                localStorage.removeItem("customerAddress");
                setIsAddressModalVisible(false);
                setTempAddress("");
                message.info("Location cleared");
              }}
              style={{ padding: 0 }}
            >
              Clear saved location
            </Button>
          )}
        </Modal>

        <Divider />
        <div className="listing-header">
          <h4 className="results-num">
            Found {businesses.length} of {businessesLength} Results
          </h4>

          <div className="sort-form">
            <form>
              <label>
                <span>Sort By</span>

                <select
                  onChange={(e) => handleSorting(e.target.value)}
                  defaultValue={sorting}
                >
                  <option value="alphabetical">Alphabetical</option>
                  <option value="latest">Latest</option>
                  <option value="highest_rated">Highest Rated</option>
                </select>
              </label>
            </form>

            <Radio.Group onChange={onChange} value={value}>
              <Radio value="asc" className="radioVal">
                ASC
              </Radio>
              <Radio value="desc" className="radioVal">
                DESC
              </Radio>
            </Radio.Group>
          </div>
        </div>

        <Pagination
          current={currentPage}
          defaultCurrent={1}
          total={businessesLength}
          pageSize={12}
          showSizeChanger={false}
          className="directory-pagination"
          itemRender={(_, type, originalElement) =>
            type === "prev" || type === "next" ? originalElement : null
          }
          onChange={(val) => setCurrentPage(val)}
        />

        <Divider />

        <div className="bizzll-directory">
          {businesses.length > 0 &&
            businesses.map((business) => {
              return (
                <ListingItem
                  key={business.id}
                  business={business}
                  userRoleAccess={userRoleAccess}
                  keyword={keyword}
                />
              );
            })}
        </div>

        <Divider />
        <div className="listing-header">
          <h4 className="results-num">
            Found {businesses.length} of {businessesLength} Results
          </h4>

          <div className="sort-form">
            <form>
              <label>
                <span>Sort By</span>

                <select
                  onChange={(e) => handleSorting(e.target.value)}
                  defaultValue={sorting}
                >
                  <option value="alphabetical">Alphabetical</option>
                  <option value="latest">Latest</option>
                  <option value="highest_rated">Highest Rated</option>
                </select>
              </label>
            </form>

            <Radio.Group onChange={onChange} value={value}>
              <Radio value="asc" className="radioVal">
                ASC
              </Radio>
              <Radio value="desc" className="radioVal">
                DESC
              </Radio>
            </Radio.Group>
          </div>
        </div>

        <Pagination
          current={currentPage}
          defaultCurrent={1}
          total={businessesLength}
          pageSize={12}
          showSizeChanger={false}
          className="directory-pagination"
          itemRender={(_, type, originalElement) =>
            type === "prev" || type === "next" ? originalElement : null
          }
          onChange={(val) => setCurrentPage(val)}
        />
      </div>
    );
  }
}
